import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';
import sortBy from 'lodash/sortBy';
// eslint-disable-next-line import/named
import { IntlFormatters } from 'react-intl';

import { filterAndReturnFilteredElements } from '../arrayutils';
import { CHARGE_POSTING_SORT_BASE_CLASS, ChargeOrder, compareChargeLabels, getChargeOrder } from '../chargesUtils';
import { Colors } from '../colorUtils';
import { addOrReplaceCustomValue } from '../customValuesUtils';
import {
  CalendarDate,
  calendarDateFromTimestamp,
  calendarDateTimeFromTimestamp,
  createCalendarDate,
  formatCalendarDate,
  fromCalendarDate,
  isAfter,
  isAfterOrEqual,
  isBefore,
  isBeforeOrEqual,
  isEqual,
  isInstantAfter,
  isInstantAfterOrEqual,
  isSameMonthAndYear,
  subtractCalendarDate,
  toCalendarDate,
  toCalendarDateNullable,
  today,
} from '../dateutils';
import { DEFAULT_LANGUAGE } from '../emailFixedTemplatesUtils';
import { getTypedLanguage } from '../i18nutils';
import { convertAmountToNegativeIfCondition, roundAtSecondDecimal } from '../numbers';
import { isNilOrEmpty } from '../objectUtils';
import { getInvoiceReferenceDate } from '../overdueUtils';
import { computeInvoicingPeriods, getNextPeriodStartDate, getStartPeriodDay } from '../periodUtils';
import {
  AccountLabel,
  Invoice,
  InvoiceDTO,
  InvoiceSetting,
  InvoiceStatus,
  InvoiceType,
  InvoiceWithPostings,
  Language,
  Lease,
  LeaseDTO,
  LeasePaymentFrequency,
  LeasePaymentInvoicePeriod,
  LeaseVariousOperationDTO,
  LeaseVariousOperationType,
  Posting,
  PostingDTO,
  PostingStatus,
  PostingType,
  Transaction,
  TransactionStatus,
  UpdateInvoiceInput,
} from '../types';
import { getMainUnitId } from '../unitutils';

import {
  ACCOUNT_CHARGES_LOSS_ON_TENANT_RECEIVABLE_CLASS,
  ACCOUNT_CHARGES_TOPCLASS,
  ACCOUNT_LEASE_TOPCLASS,
  ACCOUNT_PAYMENT_PROVIDER_TRANSIT_CLASS,
  ACCOUNT_RENTAL_GUARANTIES_RECEIVED_TOPCLASS,
  ACCOUNT_TURNOVER_CHARGES_ADJUSTEMENT_CLASS,
  ACCOUNT_TURNOVER_EXCEPTIONAL_REVENUE_CLASS,
  ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS,
  ACCOUNT_TURNOVER_FURNISHED_RENT_CLASS,
  ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS,
  ACCOUNT_TURNOVER_RENT_CLASS,
  ACCOUNT_TURNOVER_TOPCLASS,
  isMirroredSuspenseAccountClass,
} from './accountClasses';
import {
  getTransactionAmountReconciledInLeaseOrContractor,
  isPostingReversalOrCancelledByReversal,
} from './accountutils';
import { isPostingARentDiscount } from './variousOperationsUtils';

type InvoiceWithRemaining = InvoiceWithPostings & { remainingAmount: number };

// Tags of the drafts generated by the early-draft flow. The type tells them from manual drafts and picks the
// finalize schedule; the period end and the pre-draft lease state let a draft made stale be rebuilt
export const EARLY_DRAFT_INVOICE_TYPE_CUSTOM_VALUE = 'earlyDraftInvoiceType';
export const EARLY_DRAFT_PERIOD_TO_CUSTOM_VALUE = 'earlyDraftPeriodTo';
export const EARLY_DRAFT_PREVIOUS_LAST_INVOICE_DATE_CUSTOM_VALUE = 'earlyDraftPreviousLastInvoiceDate';
export const EARLY_DRAFT_PREVIOUS_OPERATION_DATES_CUSTOM_VALUE = 'earlyDraftPreviousOperationDates';
export const EARLY_DRAFT_END_LEASE_ACTION_CUSTOM_VALUE = 'earlyDraftEndLeaseAction';
/** Set by a billing item edit or a line inserted on the draft's period, valued with the line id: it is rebuilt. */
export const EARLY_DRAFT_STALE_CUSTOM_VALUE = 'earlyDraftStale';
/** Set when lines are saved by hand on an early draft, valued with the day: regenerating it would drop them. */
export const EARLY_DRAFT_HAND_EDITED_CUSTOM_VALUE = 'earlyDraftHandEdited';
/** Tags a document updateLeaseBillingItem issued, valued with the id of the line it corrects. */
export const BILLING_ITEM_CORRECTION_CUSTOM_VALUE = 'billingItemCorrection';

export const PROVISION_CREDIT_NOTE_ID_CUSTOM_VALUE = 'provisionCreditNoteId';
export const NO_PROVISION_CREDIT_NOTE = 'NONE';

export const getInvoiceCustomValue = (
  invoice: { customValues?: { name: string; value: string }[] | null },
  name: string
): string | undefined => invoice.customValues?.find((customValue) => customValue.name === name)?.value;

export const getEarlyDraftInvoiceType = (invoice: {
  customValues?: { name: string; value: string }[] | null;
}): string | undefined => getInvoiceCustomValue(invoice, EARLY_DRAFT_INVOICE_TYPE_CUSTOM_VALUE);

export const ADVANCED_CHARGES_EARLY_DRAFT_TYPE = 'ADVANCED_CHARGES';

export const isAdvancedChargesEarlyDraft = (invoice: {
  customValues?: { name: string; value: string }[] | null;
}): boolean => getEarlyDraftInvoiceType(invoice) === ADVANCED_CHARGES_EARLY_DRAFT_TYPE;

export const isEarlyDraftInvoice = (invoice: {
  status?: InvoiceStatus | null;
  customValues?: { name: string; value: string }[] | null;
}): boolean => invoice.status === InvoiceStatus.DRAFT && !isNil(getEarlyDraftInvoiceType(invoice));

/** An early draft a billing item edit may have rebuilt: never one whose lines were edited by hand. */
export const isRegenerableEarlyDraft = (invoice: {
  status?: InvoiceStatus | null;
  customValues?: { name: string; value: string }[] | null;
}): boolean =>
  isEarlyDraftInvoice(invoice) && isNil(getInvoiceCustomValue(invoice, EARLY_DRAFT_HAND_EDITED_CUSTOM_VALUE));

/**
 * An early draft a billing item edit or an inserted line changed: the finalizer rebuilds it, so it is not validated
 * by hand meanwhile.
 */
export const isEarlyDraftPendingRegeneration = (invoice: {
  status?: InvoiceStatus | null;
  customValues?: { name: string; value: string }[] | null;
}): boolean =>
  isRegenerableEarlyDraft(invoice) && !isNil(getInvoiceCustomValue(invoice, EARLY_DRAFT_STALE_CUSTOM_VALUE));

/** Tags an early draft stale, valued with the id of the line changed or inserted: the finalizer rebuilds it. */
export const buildStaleDraftUpdate = (
  invoice: Pick<Invoice, 'id' | '_version' | 'customValues'>,
  lineId: string
): UpdateInvoiceInput => ({
  id: invoice.id,
  _version: invoice._version,
  customValues: addOrReplaceCustomValue({ name: EARLY_DRAFT_STALE_CUSTOM_VALUE, value: lineId }, invoice.customValues),
});

/** A payment is settled against the draft: the finalizer issues it as drafted, never rebuilds it. */
export const isDraftSettled = (
  invoice: { paid?: boolean | null },
  draftPostings: { transactionId?: string | null }[]
): boolean => Boolean(invoice.paid) || draftPostings.some((posting) => !isNil(posting.transactionId));

// A draft created manually (no early-draft tag): must never be auto-reconciled or marked paid.
export const isManualDraftInvoice = (invoice: {
  status?: InvoiceStatus | null;
  customValues?: { name: string; value: string }[] | null;
}): boolean => invoice.status === InvoiceStatus.DRAFT && isNil(getEarlyDraftInvoiceType(invoice));

type PayableDocument = Pick<Invoice, 'paid' | 'status' | 'customValues'>;

export const isPayableLeaseDocument = (invoice: PayableDocument): boolean =>
  !invoice.paid && invoice.status !== InvoiceStatus.REVERSED && !isManualDraftInvoice(invoice);

export const isPayableDocumentPosting = (posting: Pick<Posting, 'status'>, invoice: PayableDocument): boolean =>
  !isPostingReversalOrCancelledByReversal(posting) &&
  (posting.status !== PostingStatus.DRAFT || isEarlyDraftInvoice(invoice));

/**
 * @returns the amount not covered by the postings linked with transactions on the invoice
 */
export const calculateAmountLeftToPayOfInvoice = (
  invoice: Pick<Invoice, 'amount' | 'creditNote'>,
  invoicePostings: Pick<Posting, 'invoicePostingId' | 'totalAmount' | 'type'>[]
) => {
  const invoiceAmount = getInvoiceAmount(invoice);
  const alreadyPaidAmount = invoicePostings.reduce((acc: number, posting) => {
    // A posting linked to another posting means that it corresponds to a payment on the invoice
    if (posting.invoicePostingId) acc += getPostingAmount(posting.totalAmount, posting.type);
    return acc;
  }, 0);

  // Settlements bring the balance back towards zero whatever its sign: a credit note is negative
  return roundAtSecondDecimal(invoiceAmount - Math.sign(invoiceAmount) * Math.abs(alreadyPaidAmount));
};

export const sortInvoicesByOldestDebt = <InvoiceItem extends Pick<Invoice, 'dueDate' | 'invoiceDate' | 'createdAt'>>(
  invoices: InvoiceItem[]
): InvoiceItem[] =>
  sortBy(invoices, [(invoice) => invoice.dueDate ?? invoice.invoiceDate, (invoice) => invoice.createdAt]);

export const getMatchingInvoicesWithExactAmount = (
  invoiceWithPosting: InvoiceWithPostings[],
  amount: number
): InvoiceWithRemaining[] => {
  /* Find invoices that matches the transaction amount */
  const invoiceMatches = invoiceWithPosting.map((invoice) => {
    const amountLeftToPay = calculateAmountLeftToPayOfInvoice(invoice, invoice.postings);
    return { ...invoice, remainingAmount: amountLeftToPay };
  });
  const invoicesWithRemaining = sortInvoicesByOldestDebt(invoiceMatches);
  return findCombinationOfInvoices(
    invoicesWithRemaining,
    getReachableSumBounds(invoicesWithRemaining),
    roundAtSecondDecimal(amount),
    0,
    0,
    []
  );
};

interface ReachableSumBounds {
  lowest: number[];
  highest: number[];
}

// Credit notes carry a negative remaining amount, so a partial sum above the target can still come
// back down: a branch is only abandoned once the invoices left cannot bring it back on target
const getReachableSumBounds = (invoicesWithRemaining: InvoiceWithRemaining[]): ReachableSumBounds =>
  invoicesWithRemaining.reduceRight<ReachableSumBounds>(
    ({ lowest, highest }, { remainingAmount }) => ({
      lowest: [lowest[0] + Math.min(remainingAmount, 0), ...lowest],
      highest: [highest[0] + Math.max(remainingAmount, 0), ...highest],
    }),
    { lowest: [0], highest: [0] }
  );

const findCombinationOfInvoices = (
  invoicesWithRemaining: InvoiceWithRemaining[],
  reachableSumBounds: ReachableSumBounds,
  transactionAmount: number,
  index: number,
  currentSum: number,
  selected: InvoiceWithRemaining[]
): InvoiceWithRemaining[] => {
  const roundedSum = roundAtSecondDecimal(currentSum);
  if (roundedSum === transactionAmount) {
    return selected;
  }
  const lowestReachableSum = roundAtSecondDecimal(roundedSum + reachableSumBounds.lowest[index]);
  const highestReachableSum = roundAtSecondDecimal(roundedSum + reachableSumBounds.highest[index]);
  if (lowestReachableSum > transactionAmount || highestReachableSum < transactionAmount) {
    return [];
  }

  for (let invoiceIndex = index; invoiceIndex < invoicesWithRemaining.length; invoiceIndex++) {
    const result = findCombinationOfInvoices(
      invoicesWithRemaining,
      reachableSumBounds,
      transactionAmount,
      invoiceIndex + 1,
      currentSum + invoicesWithRemaining[invoiceIndex].remainingAmount,
      [...selected, invoicesWithRemaining[invoiceIndex]]
    );
    if (!isNilOrEmpty(result)) {
      return result;
    }
  }

  return [];
};

const NOT_A_DEBT_STATUSES: (InvoiceStatus | null | undefined)[] = [InvoiceStatus.DRAFT, InvoiceStatus.REVERSED];

type IssuedDocument = Pick<Invoice, 'createdAt' | 'postedDate' | 'customValues'>;

const getIssueDay = (invoice: IssuedDocument): CalendarDate | undefined => {
  if (isNil(invoice.createdAt)) {
    return undefined;
  }
  const creationDay = calendarDateFromTimestamp(invoice.createdAt);
  if (isNilOrEmpty(invoice.postedDate) || !isNil(getEarlyDraftInvoiceType(invoice))) {
    return creationDay;
  }
  const validationDay = toCalendarDate(invoice.postedDate);
  return isAfter(validationDay, creationDay) ? validationDay : creationDay;
};

const isIssuedOnOrAfterPaymentDay = (invoice: IssuedDocument, paymentDate?: CalendarDate): boolean => {
  const issueDay = getIssueDay(invoice);
  return !isNil(paymentDate) && !isNil(issueDay) && isAfterOrEqual(issueDay, paymentDate);
};

const isIssuedBeforePaymentDay = (invoice: IssuedDocument, paymentDate?: CalendarDate): boolean => {
  const issueDay = getIssueDay(invoice);
  return !isNil(paymentDate) && !isNil(issueDay) && isBefore(issueDay, paymentDate);
};

export interface PaymentDraftContext {
  paymentDate?: CalendarDate;
  namedInvoiceIds?: string[];
}

const SEPARATE_CLAIM_INVOICE_TYPES: (InvoiceType | null | undefined)[] = [
  InvoiceType.STATEMENT,
  InvoiceType.LEASE_VARIOUS_OPERATION,
  InvoiceType.VARIOUS_EXCEPTIONAL_REVENUE,
  InvoiceType.VARIOUS_OPERATION,
  InvoiceType.VARIOUS_OPERATION_GUARANTY_CREATION,
  InvoiceType.VARIOUS_OPERATION_GUARANTY_RELEASE,
  InvoiceType.COMPANY,
];

/**
 * Leaves out the early drafts behind an open rent call due on or before them. A separate claim (charge statement,
 * one-off operation, guarantee) never holds a draft back: a payment of the next call settles that call.
 */
export const withoutDraftsBehindOpenDebt = <InvoiceItem extends InvoiceWithPostings>(
  invoices: InvoiceItem[],
  hasOpenDebt: (invoice: InvoiceItem) => boolean,
  { paymentDate, namedInvoiceIds = [] }: PaymentDraftContext = {}
): InvoiceItem[] =>
  invoices.filter((draft) => {
    if (draft.status !== InvoiceStatus.DRAFT || namedInvoiceIds.includes(draft.id)) {
      return true;
    }
    const isDraftKnownToThePayer = isIssuedBeforePaymentDay(draft, paymentDate);
    return !invoices.some(
      (invoice) =>
        !NOT_A_DEBT_STATUSES.includes(invoice.status) &&
        !SEPARATE_CLAIM_INVOICE_TYPES.includes(invoice.type) &&
        isBeforeOrEqual(getInvoiceReferenceDate(invoice), getInvoiceReferenceDate(draft)) &&
        !(isDraftKnownToThePayer && isIssuedOnOrAfterPaymentDay(invoice, paymentDate)) &&
        hasOpenDebt(invoice)
    );
  });

const hasAmountLeftToPay = (invoice: InvoiceWithPostings): boolean =>
  calculateAmountLeftToPayOfInvoice(invoice, invoice.postings) > 0;

export const getExactAmountMatchesWithoutDraftsBehindOpenDebt = (
  invoices: InvoiceWithPostings[],
  amount: number,
  paymentDraftContext?: PaymentDraftContext
): InvoiceWithRemaining[] =>
  getMatchingInvoicesWithExactAmount(
    withoutDraftsBehindOpenDebt(invoices, hasAmountLeftToPay, paymentDraftContext),
    amount
  );

export const getMostRecentInvoiceOfExactAmount = (
  invoiceWithPosting: InvoiceWithPostings[],
  amount: number,
  paymentDraftContext?: PaymentDraftContext
): InvoiceWithPostings | null => {
  const expectedAmount = roundAtSecondDecimal(amount);

  const invoiceMatches = withoutDraftsBehindOpenDebt(invoiceWithPosting, hasAmountLeftToPay, paymentDraftContext)
    .filter((invoice) => {
      const amountLeftToPay = calculateAmountLeftToPayOfInvoice(invoice, invoice.postings);
      return amountLeftToPay === expectedAmount;
    })
    .sort((invoiceA, invoiceB) => {
      const createdAtA = calendarDateTimeFromTimestamp(invoiceA.createdAt!);
      const createdAtB = calendarDateTimeFromTimestamp(invoiceB.createdAt!);

      return isInstantAfterOrEqual(createdAtA, createdAtB) ? -1 : 1;
    });

  return isEmpty(invoiceMatches) ? null : invoiceMatches[0];
};

/** Whether a proposed invoice is an early draft now held back by an open issued document: a stale proposal. */
export const hasProposedDraftBehindOpenDebt = (
  proposedInvoiceIds: string[],
  invoices: InvoiceWithPostings[],
  paymentDraftContext?: PaymentDraftContext
): boolean => {
  const matchableInvoiceIds = withoutDraftsBehindOpenDebt(invoices, hasAmountLeftToPay, paymentDraftContext).map(
    ({ id }) => id
  );
  return invoices.some(({ id }) => proposedInvoiceIds.includes(id) && !matchableInvoiceIds.includes(id));
};

export const doesPostingNeedToBePaidFromInvoice = (
  posting: Pick<Posting, 'class' | 'topClass' | 'transactionId' | 'type'>,
  invoiceType?: InvoiceType,
  isCreditNote?: boolean,
  isCreditNoteManuallyCreated?: boolean
) => {
  switch (invoiceType) {
    case InvoiceType.VARIOUS_OPERATION_GUARANTY_RELEASE:
      if (isCreditNoteManuallyCreated) {
        return posting.topClass === ACCOUNT_RENTAL_GUARANTIES_RECEIVED_TOPCLASS;
      }
      return (
        posting.class === ACCOUNT_LEASE_TOPCLASS &&
        !posting.transactionId &&
        posting.type === (isCreditNote ? PostingType.CREDIT : PostingType.DEBIT)
      );

    default:
      return (
        [ACCOUNT_TURNOVER_TOPCLASS, ACCOUNT_CHARGES_TOPCLASS, ACCOUNT_RENTAL_GUARANTIES_RECEIVED_TOPCLASS].includes(
          posting.topClass
        ) ||
        posting.class === ACCOUNT_CHARGES_LOSS_ON_TENANT_RECEIVABLE_CLASS ||
        isMirroredSuspenseAccountClass(posting.class)
      );
  }
};

// Invoices are persisted before their postings (possibly in several write batches), so a payable
// invoice whose to-pay postings do not add up to its amount yet is still mid-persist. Amounts are
// signed by posting type because discounts are DEBIT turnover lines netted in the invoice amount
export const isInvoicePostingSetComplete = (invoice: InvoiceWithPostings): boolean => {
  const postingsToPayTotal = roundAtSecondDecimal(
    (invoice.postings ?? []).reduce(
      (total, posting) =>
        doesPostingNeedToBePaid(posting) ? total + getPostingAmount(posting.totalAmount, posting.type) : total,
      0
    )
  );
  return Math.abs(postingsToPayTotal) === roundAtSecondDecimal(Math.abs(invoice.amount));
};

export const isIssuedInvoice = (invoice: Pick<Invoice, 'status' | '_deleted'>): boolean =>
  !invoice._deleted && invoice.status !== InvoiceStatus.DRAFT;

export const isOffLedgerReceivableInvoice = (postings: Pick<Posting, 'class'>[]): boolean =>
  postings.some((posting) => isMirroredSuspenseAccountClass(posting.class));

export const isNegativeAmountInvoice = (
  invoice: Pick<Invoice, 'creditNote' | 'type'>,
  invoicePostings: Pick<Posting, 'class'>[]
): boolean => {
  if (isOffLedgerReceivableInvoice(invoicePostings)) {
    return Boolean(invoice.creditNote);
  }

  return Boolean(invoice.creditNote) || invoice.type === InvoiceType.VARIOUS_OPERATION;
};

// Early drafts keep their payable postings in DRAFT until finalization; on a posted invoice a DRAFT posting is inert
const isPostingActiveOnInvoice = (posting: Posting, invoice: Pick<Invoice, 'status'>): boolean =>
  !isPostingReversalOrCancelledByReversal(posting) &&
  (invoice.status === InvoiceStatus.DRAFT || posting.status !== PostingStatus.DRAFT);

export const toInvoiceWithPostings = (invoice: Invoice, postings: Posting[]): InvoiceWithPostings => {
  const activePostings = postings.filter((posting) => isPostingActiveOnInvoice(posting, invoice));
  return {
    ...invoice,
    postings: activePostings,
    amount: isNegativeAmountInvoice(invoice, activePostings) ? -invoice.amount : invoice.amount,
  };
};

export const doesPostingNeedToBePaid = (posting: PostingDTO | Posting) =>
  doesPostingNeedToBePaidFromInvoice(
    posting,
    posting.invoice?.type,
    Boolean(posting.invoice?.creditNote),
    Boolean(posting.invoice && isCreditNoteManuallyCreated(posting.invoice))
  );

export const getPostingAmountLeftToPay = (
  postingToCalculate: PostingDTO | Posting,
  otherPostings: (PostingDTO | Posting)[]
) => {
  let amountLeftToPay = getPostingAmountFromInvoice(postingToCalculate, postingToCalculate.invoice);
  otherPostings.forEach((posting) => {
    if (posting.invoicePostingId === postingToCalculate.id) {
      const paidAmount = getPostingAmountFromInvoice(posting, posting.invoice);
      amountLeftToPay = roundAtSecondDecimal(amountLeftToPay - paidAmount);
    }
  });

  return amountLeftToPay;
};

export const getRentDiscountPostingsLeftToPay = <InvoicePosting extends PostingDTO | Posting>(
  invoicePostings: InvoicePosting[],
  unitId?: string | null
) => {
  const discountPostings = invoicePostings.filter(
    (posting) =>
      isPostingARentDiscount(posting) &&
      (isNil(unitId) || posting.unitId === unitId) &&
      getPostingAmountLeftToPay(posting, invoicePostings) !== 0
  );
  const totalAmount = roundAtSecondDecimal(
    discountPostings.reduce(
      (total, posting) => total + Math.abs(getPostingAmountLeftToPay(posting, invoicePostings)),
      0
    )
  );

  return { discountPostings, totalAmount };
};

export const getRentPostingCarryingDiscounts = <InvoicePosting extends PostingDTO | Posting>(
  invoicePostings: InvoicePosting[],
  unitId?: string | null
): InvoicePosting | undefined => {
  const rentPostings = invoicePostings.filter(
    (posting) =>
      posting.class === ACCOUNT_TURNOVER_RENT_CLASS &&
      !isPostingARentDiscount(posting) &&
      (isNil(unitId) || posting.unitId === unitId) &&
      getPostingAmountLeftToPay(posting, invoicePostings) !== 0
  );

  return orderBy(
    rentPostings,
    [(posting) => Math.abs(getPostingAmountLeftToPay(posting, invoicePostings)), 'id'],
    ['desc', 'asc']
  )[0];
};

export const getRentAmountNetOfDiscounts = (
  postingToCalculate: PostingDTO | Posting,
  invoicePostings: (PostingDTO | Posting)[]
) => {
  const amountLeftToPay = getPostingAmountLeftToPay(postingToCalculate, invoicePostings);
  const carryingPosting = getRentPostingCarryingDiscounts(invoicePostings, postingToCalculate.unitId);
  if (carryingPosting?.id !== postingToCalculate.id) {
    return amountLeftToPay;
  }
  const { totalAmount } = getRentDiscountPostingsLeftToPay(invoicePostings, postingToCalculate.unitId);

  return roundAtSecondDecimal(amountLeftToPay - totalAmount);
};

/**
 * The posting rules read the document type and credit-note flag from posting.invoice, which the light posting
 * queries do not select. A posting that already carries its invoice keeps it.
 */
export const attachInvoiceToPostings = <InvoicePosting extends Posting | PostingDTO>(
  postings: InvoicePosting[],
  invoice: Invoice | InvoiceDTO
): InvoicePosting[] => postings.map((posting) => ({ ...posting, invoice: posting.invoice ?? invoice }));

export const getPostingsWithAmountLeftToPay = <InvoicePosting extends PostingDTO | Posting>(
  invoicePostings: InvoicePosting[],
  filterPaidPostings = true
) => {
  const [postingsToPay, payingPostings] = filterAndReturnFilteredElements(invoicePostings, doesPostingNeedToBePaid);

  const postingWithAmountLeftToPay = postingsToPay.map((postingToPay) => ({
    ...postingToPay,
    totalAmount: getPostingAmountLeftToPay(postingToPay, payingPostings),
  }));

  // Keep only the postings that still need to be paid?
  return filterPaidPostings
    ? postingWithAmountLeftToPay.filter((posting) => posting.totalAmount !== 0)
    : postingWithAmountLeftToPay;
};

/**
 * Returns the posting amount, converting the amount to negative if the posting type is DEBIT.
 * @param {number|undefined|null} amount - The amount to be posted.
 * @param {PostingType} postingType - The type of posting, either DEBIT or CREDIT.
 * @returns {number} - The posting amount.
 */
export const getPostingAmount = (amount: number | undefined | null, postingType: PostingType): number => {
  if (isNil(amount)) return 0;
  return convertAmountToNegativeIfCondition(amount, postingType === PostingType.DEBIT);
};

const getGuarantyReleasePostingAmount = (guarantyReleasePosting: Posting | PostingDTO, isCreditNote: boolean) => {
  if (isNil(guarantyReleasePosting.totalAmount)) return 0;
  if (guarantyReleasePosting.class === ACCOUNT_TURNOVER_EXCEPTIONAL_REVENUE_CLASS) {
    return getPostingAmount(guarantyReleasePosting.totalAmount, guarantyReleasePosting.type);
  }
  return convertAmountToNegativeIfCondition(guarantyReleasePosting.totalAmount, isCreditNote);
};

export const getPostingAmountFromInvoice = (
  posting: Posting | PostingDTO,
  postingInvoice?: Invoice | InvoiceDTO | null
) =>
  postingInvoice?.type === InvoiceType.VARIOUS_OPERATION_GUARANTY_RELEASE
    ? getGuarantyReleasePostingAmount(posting, Boolean(postingInvoice.creditNote))
    : getPostingAmount(posting.totalAmount, posting.type);

export const getRoundedSumOfPostingAmounts = (postings: Posting[]) => {
  const totalAmount = postings.reduce((acc, currentPosting) => {
    const amount = getPostingAmount(currentPosting.totalAmount, currentPosting.type);
    acc -= amount;
    return acc;
  }, 0);

  return roundAtSecondDecimal(totalAmount);
};

export const getInvoiceAmount = (invoice: Pick<Invoice, 'amount' | 'creditNote'>) =>
  convertAmountToNegativeIfCondition(invoice.amount, Boolean(invoice.creditNote));

/**
 * Replaces the date tokens of an invoice number schema with the values of the given CalendarDate.
 * Supports the numeric date-fns tokens used by invoice schemas (yyyy, yy, MM, M, dd, d) and
 * single-quoted literals (e.g. 'INV'). Unknown characters are passed through unchanged.
 */
const INVOICE_DATE_TOKEN = /'[^']*'|''|yyyy|yyy|yy|y|MM|M|dd|d/g;
const formatInvoiceNumberDate = (date: CalendarDate, schema: string): string =>
  schema.replace(INVOICE_DATE_TOKEN, (token) => {
    if (token === "''") return "'";
    if (token.startsWith("'")) return token.slice(1, -1);
    switch (token) {
      case 'yyyy':
        return String(date.year).padStart(4, '0');
      case 'yyy':
        return String(date.year).padStart(3, '0');
      case 'yy':
        return String(date.year % 100).padStart(2, '0');
      case 'y':
        return String(date.year);
      case 'MM':
        return String(date.month).padStart(2, '0');
      case 'M':
        return String(date.month);
      case 'dd':
        return String(date.day).padStart(2, '0');
      case 'd':
        return String(date.day);
      default:
        return token;
    }
  });

/**
 * It will generate an invoice number following the schema for the given number and date.
 * @param schema The Invoice schema to replace with date and number.
 * Number in schema has to be noted with X (ex: yyyy/XX)
 * @param numberToGenerate The invoice number
 * @param date The date to fill the schema date format. Uses date-fns format tokens.
 * @returns The generated invoice number
 */
export const generateInvoiceNumber = (
  schema: string,
  numberToGenerate: number,
  date: CalendarDate = today()
): string => {
  const numberOfNumbersToFill = (schema.match(/X/g) || []).length;
  const numberFilledWithPad = `${numberToGenerate}`.padStart(numberOfNumbersToFill, '0');
  const schemaWithNumbersReplaced = schema.replace(/X+/g, numberFilledWithPad);
  return formatInvoiceNumberDate(date, schemaWithNumbersReplaced);
};

// Marker the backend puts in the error it throws when an invoice number is already taken, so the
// frontend can show a translated message instead of the raw resolver error.
export const INVOICE_NUMBER_ALREADY_USED_ERROR = 'InvoiceNumberAlreadyUsed';

// Default invoice number format schemas
export const DEFAULT_INVOICE_SCHEMA = "'INV'/yyyy/MM/XXXX";
export const DEFAULT_CREDIT_NOTE_SCHEMA = "'CN'/yyyy/MM/XXXX";
export const DEFAULT_PAYMENT_REQUEST_SCHEMA = "'PR'/yyyy/MM/XXXX";
export const DEFAULT_RECEIPT_SCHEMA = "'RCPT'/yyyy/MM/XXXX";
export const DEFAULT_VARIOUS_OPERATION_SCHEMA = "'VO'/yyyy/MM/XXXX";
export const DEFAULT_COMPANY_INVOICE_SCHEMA = "'INV'/yyyy/XXXX";
export const DEFAULT_COMPANY_CREDIT_NOTE_SCHEMA = "'CN'/yyyy/XXXX";

export const MAX_ATOMIC_TRANSACTION_ITEMS = 100;

export const MAX_BILLING_ITEMS_PER_INVOICE = Math.floor((MAX_ATOMIC_TRANSACTION_ITEMS - 3) / 4);

const FIXED_WIDTH_DATE_TOKENS: { token: string; width: number }[] = [
  { token: 'yyyy', width: 4 },
  { token: 'yy', width: 2 },
  { token: 'MM', width: 2 },
];

export const getInvoiceNumberSequenceRange = (
  schema: string
): { start: number; width: number; afterSequenceRenderedLength: number } => {
  let renderedOffset = 0;
  let i = 0;
  let sequenceStart = -1;
  let sequenceWidth = 0;
  while (i < schema.length) {
    if (schema[i] === "'") {
      const endQuoteIndex = schema.indexOf("'", i + 1);
      const literalEnd = endQuoteIndex === -1 ? schema.length : endQuoteIndex;
      renderedOffset += literalEnd - (i + 1);
      i = endQuoteIndex === -1 ? schema.length : endQuoteIndex + 1;
      continue;
    }
    if (schema[i] === 'X') {
      let width = 0;
      while (schema[i + width] === 'X') {
        width += 1;
      }
      sequenceStart = renderedOffset;
      sequenceWidth = width;
      renderedOffset += width;
      i += width;
      continue;
    }
    const dateToken = FIXED_WIDTH_DATE_TOKENS.find(({ token }) => schema.startsWith(token, i));
    if (dateToken) {
      renderedOffset += dateToken.width;
      i += dateToken.token.length;
      continue;
    }
    renderedOffset += 1;
    i += 1;
  }
  if (sequenceStart < 0) {
    return { start: -1, width: 0, afterSequenceRenderedLength: 0 };
  }
  return {
    start: sequenceStart,
    width: sequenceWidth,
    afterSequenceRenderedLength: renderedOffset - (sequenceStart + sequenceWidth),
  };
};

/**
 * Extracts the numeric sequence counter from a generated invoice number, using the schema that
 * produced it to know exactly which characters are the counter (`generateInvoiceNumber` always pads
 * the counter to the schema's X-count and substitutes it where the XXXX run sits before formatting).
 * Guessing the counter from the generated string alone (e.g. "take the trailing digit run") breaks
 * whenever a date part sits directly against the counter with no separator, or the counter isn't the
 * schema's last token (e.g. 'XXXX/yyyy') — both are real configurations the invoice number format
 * builder UI allows, since it lets the SEQUENCE part be reordered against the others.
 */
export const extractInvoiceNumberSuffix = (invoiceNumber: string, schema: string = DEFAULT_INVOICE_SCHEMA): number => {
  const { start, width, afterSequenceRenderedLength } = getInvoiceNumberSequenceRange(schema);
  if (start < 0 || width === 0) {
    return 0;
  }
  const maxEnd = invoiceNumber.length - afterSequenceRenderedLength;
  let end = start;
  while (end < maxEnd && /\d/.test(invoiceNumber[end])) {
    end += 1;
  }
  const parsed = parseInt(invoiceNumber.slice(start, end), 10);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Default field values for a brand-new InvoiceSetting, shared by every place that may need to
 * create one (sequence generator's lazy fallback, company invoice validation reserving a suffix, ...)
 * so the default schema/increment values can't drift between call sites.
 */
export const getDefaultInvoiceSettingValues = (now: string = new Date().toISOString()) => ({
  creditNoteFormat: DEFAULT_CREDIT_NOTE_SCHEMA,
  creditNoteIncrementNumber: 0,
  creditNoteLastUpdate: now,
  invoiceFormat: DEFAULT_INVOICE_SCHEMA,
  invoiceIncrementNumber: 0,
  invoiceLastUpdate: now,
  paymentRequestFormat: DEFAULT_PAYMENT_REQUEST_SCHEMA,
  paymentRequestIncrementNumber: 0,
  paymentRequestLastUpdate: now,
  receiptFormat: DEFAULT_RECEIPT_SCHEMA,
  receiptIncrementNumber: 0,
  receiptLastUpdate: now,
  variousOperationFormat: DEFAULT_VARIOUS_OPERATION_SCHEMA,
  variousOperationIncrementNumber: 0,
  variousOperationLastUpdate: now,
  customInvoicingConfiguration: true,
});

/**
 * It will return the new increment number based on given dates
 * @param currentNumber The current invoice number
 * @param lastDate The last date of invoice number generation
 * @param date The date to compare with
 * @returns The new invoice increment number (NOT INVOICE NUMBER)
 */
export const getNextIncrementNumber = (currentNumber: number, lastDate: CalendarDate, date: CalendarDate = today()) => {
  if (isSameMonthAndYear(lastDate, date)) {
    return currentNumber + 1;
  }
  // New month, new number
  return 1;
};

/**
 * The recurring day/month a fiscal year opens on. The year of the configured setting is meaningless —
 * a fiscal year restarts every 12 months on the same day/month — so only those two parts are carried.
 */
export interface FiscalYearStart {
  month: number;
  day: number;
}

/**
 * The opening date of the fiscal year that contains `date`. A start day/month that does not exist in
 * the target year (29/02 outside a leap year) is constrained down to the last valid day of the month.
 */
export const getFiscalYearStartDate = (date: CalendarDate, fiscalYearStart: FiscalYearStart): CalendarDate => {
  const openingThisYear = createCalendarDate(date.year, fiscalYearStart.month, fiscalYearStart.day);
  if (isAfter(openingThisYear, date)) {
    return createCalendarDate(date.year - 1, fiscalYearStart.month, fiscalYearStart.day);
  }
  return openingThisYear;
};

export const isSameFiscalYear = (date1: CalendarDate, date2: CalendarDate, fiscalYearStart: FiscalYearStart): boolean =>
  isEqual(getFiscalYearStartDate(date1, fiscalYearStart), getFiscalYearStartDate(date2, fiscalYearStart));

export const borrowsAgencyInvoiceSchemas = (
  issuerSetting: { customInvoicingConfiguration?: boolean | null } | undefined,
  issuerContactId: string,
  legalEntityContactId: string | null | undefined
): boolean =>
  issuerSetting?.customInvoicingConfiguration === false &&
  !isNilOrEmpty(legalEntityContactId) &&
  legalEntityContactId !== issuerContactId;

/**
 * With custom invoicing configuration off, the number schemas come from the default legal entity's row.
 * The counters always stay on the issuer's own row: numbering is unique per issuer.
 */
export const resolveInvoiceSchemaSource = (
  issuerSetting: InvoiceSetting | undefined,
  legalEntitySetting: InvoiceSetting | undefined,
  issuerContactId: string,
  legalEntityContactId: string | null | undefined
): InvoiceSetting | undefined =>
  borrowsAgencyInvoiceSchemas(issuerSetting, issuerContactId, legalEntityContactId)
    ? (legalEntitySetting ?? issuerSetting)
    : issuerSetting;

export const resolveInvoiceFiscalYearStart = async (
  schemaSource: InvoiceSetting | undefined,
  getAgencyFiscalYearStart: () => Promise<FiscalYearStart | undefined>
): Promise<FiscalYearStart | undefined> =>
  toFiscalYearStart(schemaSource?.fiscalYearStart) ?? (await getAgencyFiscalYearStart());

export const toFiscalYearStart = (value: string | null | undefined): FiscalYearStart | undefined => {
  if (isNil(value) || isEmpty(value)) {
    return undefined;
  }
  const { month, day } = toCalendarDate(value);
  return { month, day };
};

export enum InvoiceNumberResetPeriod {
  NEVER = 'NEVER',
  YEARLY = 'YEARLY',
  MONTHLY = 'MONTHLY',
}

export const getInvoiceNumberResetPeriod = (schema: string): InvoiceNumberResetPeriod => {
  // Quoted literal text (e.g. a prefix like 'IMMO') passes through format() unchanged and is never a
  // date token, even when it happens to contain characters like 'MM' — strip it before token detection
  // so a literal never gets mistaken for the actual month/year token.
  const schemaWithoutLiterals = schema.replace(/'[^']*'/g, '');
  if (schemaWithoutLiterals.includes('MM')) {
    return InvoiceNumberResetPeriod.MONTHLY;
  }
  if (schemaWithoutLiterals.includes('yyyy') || schemaWithoutLiterals.includes('yy')) {
    return InvoiceNumberResetPeriod.YEARLY;
  }
  return InvoiceNumberResetPeriod.NEVER;
};

/**
 * Whether two dates fall in the same counter reset period: the boundary a schema resets on is the date
 * token it renders (see getInvoiceNumberResetPeriod), and `fiscalYearStart` adds its own boundary on
 * top of that.
 */
export const isSameInvoiceNumberPeriod = (
  schema: string,
  date1: CalendarDate,
  date2: CalendarDate,
  fiscalYearStart?: FiscalYearStart
): boolean => {
  if (!isNil(fiscalYearStart) && !isSameFiscalYear(date1, date2, fiscalYearStart)) {
    return false;
  }
  switch (getInvoiceNumberResetPeriod(schema)) {
    case InvoiceNumberResetPeriod.MONTHLY:
      return date1.year === date2.year && date1.month === date2.month;
    case InvoiceNumberResetPeriod.YEARLY:
      // A fiscal year can span two calendar years: the check above is the yearly boundary.
      return isNil(fiscalYearStart) ? date1.year === date2.year : true;
    default:
      return true;
  }
};

/**
 * An object rather than positional arguments: a CalendarDate structurally satisfies FiscalYearStart
 * (both carry `month` and `day`), so a positional `date` could silently fill the fiscal-year slot.
 */
export interface NextIncrementNumberOptions {
  fiscalYearStart?: FiscalYearStart;
  date?: CalendarDate;
}

/**
 * Same as getNextIncrementNumber, but resets on the period the schema actually renders instead of
 * always assuming monthly — see isSameInvoiceNumberPeriod.
 */
export const getNextIncrementNumberForSchema = (
  currentNumber: number,
  lastDate: CalendarDate,
  schema: string,
  { fiscalYearStart, date = today() }: NextIncrementNumberOptions = {}
): number => (isSameInvoiceNumberPeriod(schema, lastDate, date, fiscalYearStart) ? currentNumber + 1 : 1);

export interface InvoiceNumberSequence {
  schema: string;
  incrementNumber: number;
  lastUpdate?: CalendarDate | null;
  fiscalYearStart?: FiscalYearStart;
}

export const isSequenceInCurrentPeriod = (sequence: InvoiceNumberSequence, date: CalendarDate): boolean =>
  !isNil(sequence.lastUpdate) &&
  isSameInvoiceNumberPeriod(sequence.schema, date, sequence.lastUpdate, sequence.fiscalYearStart);

export const getSchemaGeneratedSuffix = (invoiceNumber: string, schema: string, date: CalendarDate): number => {
  const suffix = extractInvoiceNumberSuffix(invoiceNumber, schema);
  return suffix > 0 && generateInvoiceNumber(schema, suffix, date) === invoiceNumber ? suffix : 0;
};

/**
 * Whether the sequence has to move forward to cover `suffix`. False when the current period already
 * covers it, so a lower explicit number can never rewind a counter.
 */
export const shouldAdvanceSequenceToSuffix = (
  sequence: InvoiceNumberSequence,
  suffix: number,
  date: CalendarDate
): boolean => !(isSequenceInCurrentPeriod(sequence, date) && suffix <= sequence.incrementNumber);

export const getPostingLabel = (posting: Posting, language: string, accountLabels: AccountLabel[]): string => {
  if (!isNilOrEmpty(posting.customLabel)) {
    return posting.customLabel;
  }
  if (posting.leaseVariousOperation) {
    return posting.leaseVariousOperation.label ?? '';
  }
  if (posting.accountLabel) {
    const accountLabelWithSameId = accountLabels.find((accountLabel) => accountLabel.id === posting.accountLabel?.id);
    return accountLabelWithSameId ? getAccountLanguageByLanguage(accountLabelWithSameId, language) : '';
  }

  return '';
};

export const POSTING_LABEL_PREFIX_SEPARATOR = ' - ';

export const getPostingPrefixLabel = (posting: Posting, formatMessage: IntlFormatters['formatMessage']) => {
  if (posting.leaseVariousOperation && posting.invoice?.type !== InvoiceType.LEASE_VARIOUS_OPERATION) {
    return formatMessage({ id: `enums.LeaseVariousOperationType.${posting.leaseVariousOperation.operationType}` });
  }
  return null;
};

const getChargeAccountClassForOperationType = (operationType: LeaseVariousOperationType): number | null => {
  switch (operationType) {
    case LeaseVariousOperationType.FIXED_CHARGE:
      return ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS;
    case LeaseVariousOperationType.PROVISIONED_CHARGE:
      return ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS;
    default:
      return null;
  }
};

const isPostingLabelRedundantWithPrefix = (posting: Posting, postingLabel: string, accountLabels: AccountLabel[]) => {
  const operationType = posting.leaseVariousOperation?.operationType;
  if (isNil(operationType)) {
    return false;
  }
  const chargeAccountClass = getChargeAccountClassForOperationType(operationType);
  const accountLabel = accountLabels.find((currentAccountLabel) => currentAccountLabel.id === posting.accountLabelId);
  if (isNil(chargeAccountClass) || isNil(accountLabel) || accountLabel.class !== chargeAccountClass) {
    return false;
  }
  return (accountLabel.labels ?? []).some((customLabel) => customLabel.label === postingLabel);
};

const getPostingBaseLabel = (
  posting: Posting,
  prefixLabel: string | null,
  postingLabel: string,
  accountLabels: AccountLabel[]
) => {
  if (isNilOrEmpty(prefixLabel)) {
    return postingLabel;
  }
  if (isNilOrEmpty(postingLabel) || isPostingLabelRedundantWithPrefix(posting, postingLabel, accountLabels)) {
    return prefixLabel;
  }
  return `${prefixLabel}${POSTING_LABEL_PREFIX_SEPARATOR}${postingLabel}`;
};

const getVatRatePercentForPostingDisplay = (posting: Posting): number | null => {
  const vatRate = posting.vatRate?.value ?? posting.vatRateValue;
  if (!isNil(vatRate)) {
    return vatRate;
  }
  return null;
};

const shouldDisplayVatRateOnPostingName = (posting: Posting) =>
  (posting.invoice?.type === InvoiceType.STATEMENT || !isNil(posting.statementId)) &&
  posting.class === ACCOUNT_TURNOVER_CHARGES_ADJUSTEMENT_CLASS &&
  !isNil(posting.vatRateId);

export const getPostingFullNameToDisplay = (
  posting: Posting,
  language: string,
  accountLabels: AccountLabel[],
  formatMessage: IntlFormatters['formatMessage']
) => {
  const prefixLabel = getPostingPrefixLabel(posting, formatMessage);
  const postingLabel = getPostingLabel(posting, language, accountLabels);
  const baseLabel = getPostingBaseLabel(posting, prefixLabel, postingLabel, accountLabels);
  if (!shouldDisplayVatRateOnPostingName(posting)) {
    return baseLabel;
  }
  const vatRatePercent = getVatRatePercentForPostingDisplay(posting);
  if (isNil(vatRatePercent)) {
    return baseLabel;
  }
  return `${baseLabel} (${vatRatePercent}%)`;
};

export const getAccountLanguageByLanguage = (accountLabel: AccountLabel, language: string) => {
  const label = accountLabel.labels?.find(
    (currentLabel) => getTypedLanguage(currentLabel.language) === getTypedLanguage(language)
  );
  const defaultLabel = label || accountLabel.labels?.find((currentLabel) => currentLabel.language === Language.EN);
  return defaultLabel ? defaultLabel.label : '';
};

export const hasAtLeastOneUnpaidInvoice = (invoices: Invoice[] | InvoiceWithPostings[]) => {
  const currentDate = today();
  return invoices.some(
    (invoice) =>
      invoice.status === InvoiceStatus.INVOICED &&
      invoice.paid === false &&
      invoice.dueDate &&
      isBefore(toCalendarDate(invoice.dueDate), currentDate)
  );
};

const buildChargeOrderByLeaseVariousOperationId = (lease?: Lease | LeaseDTO): Map<string, ChargeOrder> => {
  const leaseVariousOperations = isArray(lease?.variousOperations)
    ? lease.variousOperations
    : ((lease as LeaseDTO | undefined)?.variousOperations?.items ?? []);
  return leaseVariousOperations.reduce((accumulator, leaseVariousOperation) => {
    if (!isNil(leaseVariousOperation)) {
      accumulator.set(leaseVariousOperation.id, getChargeOrder(leaseVariousOperation));
    }
    return accumulator;
  }, new Map<string, ChargeOrder>());
};

const getPostingClassSortValue = (
  posting: Posting | PostingDTO,
  chargeOrderByLeaseVariousOperationId: Map<string, ChargeOrder>
): number => {
  if (
    posting.class !== ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS &&
    posting.class !== ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS
  ) {
    return posting.class;
  }
  const defaultChargeOrder =
    posting.class === ACCOUNT_TURNOVER_FIXED_CHARGES_CLASS
      ? ChargeOrder.FIXED_CHARGE
      : ChargeOrder.GENERAL_PROVISIONED_CHARGE;
  const chargeOrder = isNil(posting.leaseVariousOperationId)
    ? defaultChargeOrder
    : (chargeOrderByLeaseVariousOperationId.get(posting.leaseVariousOperationId) ?? defaultChargeOrder);
  return CHARGE_POSTING_SORT_BASE_CLASS + chargeOrder;
};

export const sortPostings = <T extends Posting | PostingDTO>(postings: T[], lease?: Lease | LeaseDTO): T[] => {
  const mainUnitId = lease ? getMainUnitId(lease) : -1;
  const language = lease?.language ?? DEFAULT_LANGUAGE;
  const chargeOrderByLeaseVariousOperationId = buildChargeOrderByLeaseVariousOperationId(lease);

  const earliestDateByLabel = new Map<string | null, CalendarDate>();
  postings.forEach((posting) => {
    const label = posting.customLabel ?? null;
    const date = toCalendarDate(posting.periodFrom);
    const currentEarliest = earliestDateByLabel.get(label);
    if (!currentEarliest || isBefore(date, currentEarliest)) {
      earliestDateByLabel.set(label, date);
    }
  });

  const labelRankByLabel = [...earliestDateByLabel.keys()]
    .sort((leftLabel, rightLabel) => compareChargeLabels(leftLabel, rightLabel, language))
    .reduce((accumulator, label, index) => accumulator.set(label, index), new Map<string | null, number>());

  return orderBy(
    postings,
    [
      (posting) => (posting.unitId === mainUnitId ? 0 : 1),
      'unit.name',
      (posting) => getPostingClassSortValue(posting, chargeOrderByLeaseVariousOperationId),
      'type',
      'accountLabel.customUsage',
      (posting) => {
        const earliestDate = earliestDateByLabel.get(posting.customLabel ?? null);
        return earliestDate ? fromCalendarDate(earliestDate) : '';
      },
      (posting) => labelRankByLabel.get(posting.customLabel ?? null) ?? 0,
      'periodFrom',
    ],
    ['asc', 'asc', 'asc', 'asc', 'desc', 'asc', 'asc', 'asc']
  );
};

export interface ColorWithId {
  color: string;
  id: InvoiceStatusId;
}

export enum InvoiceStatusId {
  PAID = 'tenant.paid',
  PARTIALLY_PAID = 'tenant.partiallyPaid',
  UNPAID = 'tenant.unpaid',
  IN_TRANSIT = 'transactions.lettering.inTransit',
}
// must be a method to avoid undefined 'Colors'
export const getInvoiceStatusIdsWithColor = (): Record<string, ColorWithId> => ({
  paid: { color: Colors.TURQUOISE_BLUE, id: InvoiceStatusId.PAID },
  partiallyPaid: { color: Colors.CARROT_ORANGE, id: InvoiceStatusId.PARTIALLY_PAID },
  unpaid: { color: Colors.CARNATION_RED, id: InvoiceStatusId.UNPAID },
  inTransit: { color: Colors.DODGER_BLUE, id: InvoiceStatusId.IN_TRANSIT },
});

export interface InvoiceStatusIdAndColor {
  color: string;
  id: InvoiceStatus;
}

export const getPostingStatusIdWithColor = (
  paidAmount: number,
  totalAmount: number,
  accountClass?: number,
  isInvoicePaid?: boolean
): ColorWithId => {
  const invoiceStatusIdWithColor = getInvoiceStatusIdsWithColor();
  if (totalAmount === 0) {
    return invoiceStatusIdWithColor.paid;
  }
  if (accountClass === ACCOUNT_PAYMENT_PROVIDER_TRANSIT_CLASS && paidAmount === 0) {
    return invoiceStatusIdWithColor.inTransit;
  }
  if (paidAmount === 0 && isInvoicePaid) {
    return invoiceStatusIdWithColor.inTransit;
  }
  if (paidAmount === totalAmount) {
    return invoiceStatusIdWithColor.paid;
  }
  if (paidAmount === 0) {
    return invoiceStatusIdWithColor.unpaid;
  }
  return invoiceStatusIdWithColor.partiallyPaid;
};

const hasInvoicePaidPostings = (invoice: InvoiceWithPostings): boolean =>
  invoice.postings.some(
    (posting) => !isNil(posting.invoicePostingId) && !isPostingReversalOrCancelledByReversal(posting)
  );

export const isInvoiceInTransit = (invoice: InvoiceWithPostings): boolean => {
  if (!invoice.paid) {
    return false;
  }
  const paymentProperties = invoice.integrations?.paymentProperties;
  const isPayoutPending = !isNilOrEmpty(paymentProperties?.statementDescriptor) && isNil(paymentProperties?.payoutId);
  const isPaymentIntentPending = !isNilOrEmpty(paymentProperties?.paymentIntentId) && !hasInvoicePaidPostings(invoice);
  return isPayoutPending || isPaymentIntentPending;
};

export const getInvoiceStatusIdWithColor = (invoice: InvoiceWithPostings): ColorWithId => {
  const invoiceStatusIdWithColor = getInvoiceStatusIdsWithColor();
  if (invoice.amount === 0) {
    return invoiceStatusIdWithColor.paid;
  }
  const hasPaidPostings = hasInvoicePaidPostings(invoice);
  if (invoice.paid) {
    if (isInvoiceInTransit(invoice)) {
      return invoiceStatusIdWithColor.inTransit;
    }
    return invoiceStatusIdWithColor.paid;
  }
  if (hasPaidPostings) {
    return invoiceStatusIdWithColor.partiallyPaid;
  }
  return invoiceStatusIdWithColor.unpaid;
};

export const isInvoicePaidViaPaymentProvider = (invoice: Invoice | InvoiceDTO): boolean =>
  !isNilOrEmpty(invoice.integrations?.paymentProperties?.paymentIntentId);

export const getInvoiceCurrentStatusIdWithColor = (status: InvoiceStatus): InvoiceStatusIdAndColor => {
  switch (status) {
    case InvoiceStatus.DRAFT:
      return { color: Colors.CARROT_ORANGE, id: InvoiceStatus.DRAFT };
    case InvoiceStatus.INVOICED:
      return { color: Colors.TURQUOISE_BLUE, id: InvoiceStatus.INVOICED };
    case InvoiceStatus.POSTED:
      return { color: Colors.DODGER_BLUE, id: InvoiceStatus.POSTED };
    case InvoiceStatus.PAYMENT_FAILED:
      return { color: Colors.CARNATION_RED, id: InvoiceStatus.PAYMENT_FAILED };
    case InvoiceStatus.REVERSED:
      return { color: Colors.STRIPE_PURPLE, id: InvoiceStatus.REVERSED };
  }
};

export const getTranslationIdAndColor = (transaction: Transaction, linkId: string): { color: string; id: string } => {
  const toReconcileIdAndColor = { color: Colors.CARNATION_RED, id: 'transactions.lettering.unReconciledHelper' };
  const reconcileIdAndColor = { color: Colors.TURQUOISE_BLUE, id: 'transactions.lettering.reconciledHelper' };
  const partiallyReconciledIdAndColor = {
    color: Colors.CARROT_ORANGE,
    id: 'transactions.lettering.partiallyReconciled',
  };
  switch (transaction.status) {
    case TransactionStatus.RECONCILED:
      return reconcileIdAndColor;
    case TransactionStatus.TO_RECONCILE:
      return toReconcileIdAndColor;
    case TransactionStatus.PARTIALLY_RECONCILED:
      const reconciledAmount = getTransactionAmountReconciledInLeaseOrContractor(transaction, linkId);
      const transactionAmountLinkedToLease = transaction.links?.find((link) => link.linkId === linkId)?.amount ?? 0;
      if (reconciledAmount === 0) {
        return toReconcileIdAndColor;
      }
      if (reconciledAmount === transactionAmountLinkedToLease) {
        return reconcileIdAndColor;
      }
      return partiallyReconciledIdAndColor;
    default:
      return partiallyReconciledIdAndColor;
  }
};

export const isCreditNoteManuallyCreated = (invoice: Invoice | InvoiceDTO) =>
  Boolean(invoice.creditNote && invoice.type === InvoiceType.RENT);

const RENT_REVENUE_CLASSES = [ACCOUNT_TURNOVER_RENT_CLASS, ACCOUNT_TURNOVER_FURNISHED_RENT_CLASS];
const PROVISION_REVENUE_CLASSES = [ACCOUNT_TURNOVER_PROVISIONED_CHARGES_CLASS];
const STATEMENT_REVENUE_CLASSES = [ACCOUNT_TURNOVER_CHARGES_ADJUSTEMENT_CLASS];

export enum ManualCreditNoteRevenueCategory {
  RENT = 'RENT',
  PROVISION = 'PROVISION',
  STATEMENT = 'STATEMENT',
}

const getRevenueCategoryForAccountClass = (accountClass: number): ManualCreditNoteRevenueCategory | undefined => {
  if (RENT_REVENUE_CLASSES.includes(accountClass)) {
    return ManualCreditNoteRevenueCategory.RENT;
  }
  if (PROVISION_REVENUE_CLASSES.includes(accountClass)) {
    return ManualCreditNoteRevenueCategory.PROVISION;
  }
  if (STATEMENT_REVENUE_CLASSES.includes(accountClass)) {
    return ManualCreditNoteRevenueCategory.STATEMENT;
  }
  return undefined;
};

export const getManualCreditNoteRevenueCategory = (
  postings: Pick<Posting, 'class' | 'topClass'>[]
): ManualCreditNoteRevenueCategory | undefined => {
  const revenuePostings = postings.filter((posting) => posting.topClass === ACCOUNT_TURNOVER_TOPCLASS);
  if (isEmpty(revenuePostings)) {
    return undefined;
  }
  const revenueCategories = [
    ...new Set(revenuePostings.map((posting) => getRevenueCategoryForAccountClass(posting.class))),
  ];
  return revenueCategories.length === 1 ? revenueCategories[0] : undefined;
};

export const getManualCreditNoteTitleMessageId = (postings: Pick<Posting, 'class' | 'topClass'>[]): string => {
  const revenueCategory = getManualCreditNoteRevenueCategory(postings);
  if (!isNil(revenueCategory)) {
    return `enums.CreditNoteInvoiceType.${revenueCategory}`;
  }
  return `enums.InvoiceDocumentName.CREDIT_NOTE`;
};

const CREDIT_NOTE_INVOICE_TYPES = [
  InvoiceType.RENT,
  InvoiceType.STATEMENT,
  InvoiceType.LEASE_VARIOUS_OPERATION,
  InvoiceType.VARIOUS_EXCEPTIONAL_REVENUE,
  InvoiceType.VARIOUS_OPERATION_GUARANTY_CREATION,
  InvoiceType.VARIOUS_OPERATION_GUARANTY_RELEASE,
];

const getCreditNoteTitle = (
  invoice: InvoiceWithPostings | Invoice,
  postings: Pick<Posting, 'class' | 'topClass'>[],
  formatMessage: IntlFormatters['formatMessage']
) => {
  if (isCreditNoteManuallyCreated(invoice)) {
    return formatMessage({ id: getManualCreditNoteTitleMessageId(postings) });
  }
  if (CREDIT_NOTE_INVOICE_TYPES.includes(invoice.type)) {
    return formatMessage({ id: `enums.CreditNoteInvoiceType.${invoice.type}` });
  }
  return formatMessage({ id: `enums.InvoiceDocumentName.CREDIT_NOTE` });
};

export const getInvoiceTitle = (
  invoice: InvoiceWithPostings | Invoice,
  leaseIsVatLiable: boolean,
  formatMessage: IntlFormatters['formatMessage']
) => {
  if (invoice.type === InvoiceType.LEASE_VARIOUS_OPERATION && invoice.creditNote) {
    const isBillingItemCorrection = !isNil(getInvoiceCustomValue(invoice, BILLING_ITEM_CORRECTION_CUSTOM_VALUE));
    return formatMessage({
      id: isBillingItemCorrection
        ? `enums.InvoiceDocumentName.CREDIT_NOTE`
        : `enums.LeaseVariousOperationType.DISCOUNT`,
    });
  }

  const postings = ('postings' in invoice ? invoice.postings : undefined) ?? [];
  const isReceivableInvoice = isOffLedgerReceivableInvoice(postings);
  const receivableTitle = formatMessage({ id: `enums.InvoiceDocumentName.OUTSTANDING_RECEIVABLE` });

  if (invoice.creditNote) {
    const creditNoteTitle = getCreditNoteTitle(invoice, postings, formatMessage);
    if (isReceivableInvoice) {
      return `${receivableTitle} - ${creditNoteTitle}`;
    }
    return creditNoteTitle;
  }

  if (isReceivableInvoice) {
    return receivableTitle;
  }

  if (
    [
      InvoiceType.VARIOUS_OPERATION,
      InvoiceType.VARIOUS_OPERATION_GUARANTY_CREATION,
      InvoiceType.VARIOUS_OPERATION_GUARANTY_RELEASE,
      InvoiceType.VARIOUS_EXCEPTIONAL_REVENUE,
    ].includes(invoice.type)
  ) {
    return formatMessage({ id: `enums.InvoiceDocumentName.${invoice.type}` });
  }

  if (leaseIsVatLiable) {
    return formatMessage({ id: `enums.InvoiceDocumentName.INVOICE` });
  }

  return formatMessage({ id: `enums.InvoiceDocumentName.PAYMENT_REQUEST` });
};

const compareInvoiceCreationDates = (firstInvoice: Invoice, secondInvoice: Invoice): number => {
  if (isNil(firstInvoice.createdAt) || isNil(secondInvoice.createdAt)) {
    return 0;
  }
  const firstCreatedAt = calendarDateTimeFromTimestamp(firstInvoice.createdAt);
  const secondCreatedAt = calendarDateTimeFromTimestamp(secondInvoice.createdAt);
  if (isInstantAfter(firstCreatedAt, secondCreatedAt)) {
    return -1;
  }
  return 1;
};

export const sortInvoicesByPeriodAndCreationOrder = <T extends Invoice>(invoices: T[]): T[] =>
  [...invoices].sort((firstInvoice, secondInvoice) => {
    const firstInvoiceDate = toCalendarDate(firstInvoice.invoiceDate);
    const secondInvoiceDate = toCalendarDate(secondInvoice.invoiceDate);
    if (!isSameMonthAndYear(firstInvoiceDate, secondInvoiceDate)) {
      return isBefore(firstInvoiceDate, secondInvoiceDate) ? 1 : -1;
    }
    return compareInvoiceCreationDates(firstInvoice, secondInvoice);
  });

export const getTransactionsInPendingBalance = (allLeaseTransactions: Transaction[]) => {
  const allTransactionIdsInPendingBalance = allLeaseTransactions.reduce((acc, transaction) => {
    if (transaction.status !== TransactionStatus.IGNORE || transaction.amount === 0) {
      return acc;
    }
    const idsToAdd =
      transaction.postings?.reduce((allForeignIds, posting) => {
        if (posting.foreignId) {
          allForeignIds.push(posting.foreignId);
        }
        return allForeignIds;
      }, [] as string[]) ?? [];
    acc.push(...idsToAdd);
    return acc;
  }, [] as string[]);

  return allLeaseTransactions.filter((transaction) => allTransactionIdsInPendingBalance.includes(transaction.id));
};

export interface PeriodOption {
  formattedPeriod: string;
  periodLastInvoiceDate: CalendarDate | null;
  periodMaybeAlreadyInvoiced: boolean;
  periodFrom: CalendarDate;
  periodTo: CalendarDate;
  dueDate: CalendarDate;
}

export const formatInvoicePeriod = (
  periodStartDate: CalendarDate,
  periodEndDate: CalendarDate,
  language: Language
): string => {
  const formattedStart = formatCalendarDate(periodStartDate, language);
  const formattedEnd = formatCalendarDate(periodEndDate, language);

  return `${formattedStart} → ${formattedEnd}`;
};

export const getCurrentCalendarPeriodStart = (
  operationStartDate: CalendarDate,
  paymentFrequency: LeasePaymentFrequency
): CalendarDate => {
  return getStartPeriodDay(operationStartDate, LeasePaymentInvoicePeriod.CALENDAR_PERIOD, undefined, paymentFrequency);
};

export const getCurrentCalendarPeriodEnd = (
  currentDate: CalendarDate,
  paymentFrequency: LeasePaymentFrequency
): CalendarDate => {
  const nextStart = getNextPeriodStartDate(
    currentDate,
    LeasePaymentInvoicePeriod.CALENDAR_PERIOD,
    undefined,
    paymentFrequency
  );
  return subtractCalendarDate(nextStart, 1, 'days');
};

export const calculateEndDateForDuration = (
  startDate: CalendarDate,
  durationInPeriods: number,
  paymentFrequency: LeasePaymentFrequency,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod
): CalendarDate => {
  let nextStart = startDate;

  const customDay = paymentInvoicePeriod === LeasePaymentInvoicePeriod.CUSTOM_PERIOD ? startDate.day : undefined;

  for (let index = 0; index < durationInPeriods; index++) {
    nextStart = getNextPeriodStartDate(nextStart, paymentInvoicePeriod, customDay, paymentFrequency);
  }

  return subtractCalendarDate(nextStart, 1, 'days');
};

export interface LeasePaymentInvoiceSettings {
  paymentFrequency: LeasePaymentFrequency;
  paymentInvoicePeriod: LeasePaymentInvoicePeriod;
  paymentInvoicePeriodCustomDay: number | null;
}

export const calculateInvoicePeriod = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  leasePaymentInvoiceSettings: LeasePaymentInvoiceSettings,
  lastInvoiceDate?: CalendarDate | null
): {
  invoiceDate: CalendarDate | undefined;
  periodFrom: CalendarDate | undefined;
  periodTo: CalendarDate | undefined;
} => {
  const periods = computeInvoicingPeriods(
    startDate,
    endDate,
    leasePaymentInvoiceSettings.paymentInvoicePeriod,
    leasePaymentInvoiceSettings.paymentFrequency,
    leasePaymentInvoiceSettings.paymentInvoicePeriodCustomDay || undefined,
    lastInvoiceDate ?? undefined,
    1
  );
  const { periodFrom, periodTo } = periods[0] || {};

  return { invoiceDate: periodFrom, periodFrom, periodTo };
};

/** The period an early draft bills: its stream's settings, read from the cursor the lease had when it was drafted. */
export const calculateEarlyDraftPeriod = (
  lease: Pick<
    LeaseDTO,
    | 'startDate'
    | 'endDate'
    | 'paymentFrequency'
    | 'paymentInvoicePeriod'
    | 'paymentInvoicePeriodCustomDay'
    | 'advancedChargesInvoices'
  >,
  invoice: { customValues?: { name: string; value: string }[] | null }
): ReturnType<typeof calculateInvoicePeriod> | undefined => {
  const settings = isAdvancedChargesEarlyDraft(invoice) ? lease.advancedChargesInvoices : lease;
  if (isNil(settings)) {
    return undefined;
  }
  const previousLastInvoiceDate = getInvoiceCustomValue(invoice, EARLY_DRAFT_PREVIOUS_LAST_INVOICE_DATE_CUSTOM_VALUE);
  return calculateInvoicePeriod(
    toCalendarDate(lease.startDate),
    toCalendarDate(lease.endDate),
    {
      paymentFrequency: settings.paymentFrequency,
      paymentInvoicePeriod: settings.paymentInvoicePeriod,
      paymentInvoicePeriodCustomDay: settings.paymentInvoicePeriodCustomDay ?? null,
    },
    isNilOrEmpty(previousLastInvoiceDate) ? null : toCalendarDate(previousLastInvoiceDate)
  );
};

export const getDateAndPeriodsForLeaseVariousOperationInvoice = (
  leaseVariousOperation: Pick<
    LeaseVariousOperationDTO,
    | 'startDate'
    | 'endDate'
    | 'paymentFrequency'
    | 'paymentInvoicePeriod'
    | 'paymentInvoicePeriodCustomDay'
    | 'lastInvoiceDate'
  >
) =>
  calculateInvoicePeriod(
    toCalendarDate(leaseVariousOperation.startDate),
    toCalendarDate(leaseVariousOperation.endDate),
    {
      paymentFrequency: leaseVariousOperation.paymentFrequency,
      paymentInvoicePeriod: leaseVariousOperation.paymentInvoicePeriod,
      paymentInvoicePeriodCustomDay: leaseVariousOperation.paymentInvoicePeriodCustomDay ?? null,
    },
    toCalendarDateNullable(leaseVariousOperation.lastInvoiceDate)
  );

export const generateInvoicePeriodsWithEffectiveLastInvoiceDate = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  leasePaymentInvoiceSettings: LeasePaymentInvoiceSettings,
  language: Language,
  leaseLastInvoiceDate?: CalendarDate | null,
  numberOfPeriods: number = 3
): PeriodOption[] => {
  return generateInvoicePeriods(
    startDate,
    endDate,
    leasePaymentInvoiceSettings,
    language,
    leaseLastInvoiceDate,
    numberOfPeriods
  );
};

export const generateInvoicePeriods = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  leasePaymentInvoiceSettings: LeasePaymentInvoiceSettings,
  language: Language,
  leaseLastInvoiceDate?: CalendarDate | null,
  numberOfPeriods: number = 3
): PeriodOption[] => {
  const periods = computeInvoicingPeriods(
    startDate,
    endDate,
    leasePaymentInvoiceSettings.paymentInvoicePeriod,
    leasePaymentInvoiceSettings.paymentFrequency,
    leasePaymentInvoiceSettings.paymentInvoicePeriodCustomDay || undefined,
    leaseLastInvoiceDate || undefined,
    numberOfPeriods,
    today()
  );

  return periods.map(({ periodFrom, periodTo, previousPeriodStart }) => ({
    formattedPeriod: formatInvoicePeriod(periodFrom, periodTo, language),
    periodLastInvoiceDate: previousPeriodStart || null,
    periodMaybeAlreadyInvoiced: isBefore(periodTo, leaseLastInvoiceDate ?? startDate),
    periodFrom,
    periodTo,
    dueDate: periodFrom,
  }));
};

export const getAnteriorInvoicePeriods = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  leasePaymentInvoiceSettings: LeasePaymentInvoiceSettings,
  language: Language,
  firstBillingPeriodStart: CalendarDate | null
): PeriodOption[] => {
  if (isNil(firstBillingPeriodStart)) {
    return [];
  }
  const periods = computeInvoicingPeriods(
    startDate,
    endDate,
    leasePaymentInvoiceSettings.paymentInvoicePeriod,
    leasePaymentInvoiceSettings.paymentFrequency,
    leasePaymentInvoiceSettings.paymentInvoicePeriodCustomDay || undefined
  );

  return periods.reduce<PeriodOption[]>((anteriorPeriods, { periodFrom, periodTo, previousPeriodStart }) => {
    if (!isBefore(periodFrom, firstBillingPeriodStart)) {
      return anteriorPeriods;
    }
    anteriorPeriods.push({
      formattedPeriod: formatInvoicePeriod(periodFrom, periodTo, language),
      periodLastInvoiceDate: previousPeriodStart ?? null,
      periodMaybeAlreadyInvoiced: true,
      periodFrom,
      periodTo,
      dueDate: periodFrom,
    });
    return anteriorPeriods;
  }, []);
};

export const shouldPostingBeDisplayedWithoutBalanceImpact = (
  posting: Posting | PostingDTO,
  invoiceType: InvoiceType
) => {
  switch (invoiceType) {
    case InvoiceType.VARIOUS_OPERATION_GUARANTY_RELEASE:
      return posting.class === ACCOUNT_TURNOVER_EXCEPTIONAL_REVENUE_CLASS;
    default:
      return false;
  }
};

export const getPostingsWithoutBalanceImpact = (invoiceWithPostings: InvoiceWithPostings) =>
  invoiceWithPostings.postings.filter((posting) =>
    shouldPostingBeDisplayedWithoutBalanceImpact(posting, invoiceWithPostings.type)
  );

export const getInvoiceAmounts = (invoiceWithPostings: InvoiceWithPostings, ignoredPostingClasses: number[] = []) => {
  const isCreditNoteOrVariousOperation =
    invoiceWithPostings.creditNote ||
    (invoiceWithPostings.type === InvoiceType.VARIOUS_OPERATION &&
      !isOffLedgerReceivableInvoice(invoiceWithPostings.postings));
  const postingsWithAmountLeftToPay = getPostingsWithAmountLeftToPay(invoiceWithPostings.postings, false).filter(
    (posting) => !ignoredPostingClasses.includes(posting.class)
  );

  const amountNotPaidYet = convertAmountToNegativeIfCondition(
    Math.abs(
      roundAtSecondDecimal(postingsWithAmountLeftToPay.reduce((total, posting) => total + posting.totalAmount, 0))
    ),
    isCreditNoteOrVariousOperation
  );

  const amountToPayToShow = convertAmountToNegativeIfCondition(
    invoiceWithPostings.amount,
    isCreditNoteOrVariousOperation
  );
  const amountPaidToShow = amountToPayToShow - amountNotPaidYet;

  return { amountToPayToShow, amountPaidToShow, postingsWithAmountLeftToPay };
};

export const getInvoiceAmountFromLinesTotal = (invoiceAmount: number, linesTotal: number): number =>
  invoiceAmount < 0 ? -Math.abs(linesTotal) : Math.abs(linesTotal);

export const PURCHASE_ORDER_NUMBER_MAX_LENGTH = 50;

export const getPurchaseOrderNumberSnapshot = (purchaseOrderNumber: string | null | undefined): string | undefined => {
  const trimmedPurchaseOrderNumber = purchaseOrderNumber?.trim();
  return isNilOrEmpty(trimmedPurchaseOrderNumber) ? undefined : trimmedPurchaseOrderNumber;
};
